/**
 * Browser capability detection.
 *
 * clip2commons relies on three browser features that are not yet universal:
 *
 *  1. WebCodecs `VideoEncoder` for re-encoding non-WebM source files.
 *     Missing on Firefox for Android (no roadmap as of 2026), partial on
 *     older Safari (full only in 26.1+), and behind a flag historically on
 *     Firefox desktop.
 *
 *  2. Dedicated Web Workers (used to run the encoder off the main thread).
 *     Universally supported on real browsers, but absent in some embedded
 *     WebViews and aggressive privacy modes.
 *
 *  3. IndexedDB (used to persist resume state). Available basically
 *     everywhere except Firefox in private mode.
 *
 * If `canEncode` is false but `canPassthrough` is true, the tool is still
 * useful for already-VP9-WebM files (which skip the encoder anyway).
 *
 * If neither, the user gets a clear "this browser is not supported" page
 * with guidance to use a different browser or video2commons instead.
 */

export interface Capabilities {
  /** Can re-encode arbitrary input video to VP9 WebM in the browser. */
  canEncode: boolean
  /** Can upload an already-VP9-WebM file as-is (no encoder needed). */
  canPassthrough: boolean
  /** IndexedDB available - required for cross-session resume. */
  canResume: boolean
  /** A short reason string when canEncode is false. */
  reason: string
  /**
   * Effective max file size in bytes, possibly lower than the configured
   * ceiling on memory-constrained devices. Encoding holds the entire output
   * buffer in main-thread memory through the upload phase; on a 3 GB phone
   * with the rest of Chrome already eating 1.2 GB resident, a 500 MB input
   * + ~200 MB output combo will OOM the tab. Cap at 200 MB on those.
   */
  effectiveMaxBytes: number
  /** Detailed details for diagnostic display. */
  details: {
    hasWebCodecs: boolean
    hasVideoEncoder: boolean
    vp9Supported: boolean | null
    hasWorker: boolean
    hasIndexedDB: boolean
    isSecureContext: boolean
    /** navigator.deviceMemory - only Chromium-family browsers expose it.
     *  Returns approximate RAM in GiB rounded down to a power of 2 (so
     *  values are 0.25, 0.5, 1, 2, 4, 8). undefined elsewhere. */
    deviceMemoryGiB: number | undefined
    /** True if the limit is being enforced because of low memory. */
    isIOSDevice: boolean
    memoryLimited: boolean
  }
}

let cached: Capabilities | null = null

/** Run capability detection. Result is memoized; calling again returns the
 *  same object. Safe to call before the first user interaction. */
/**
 * Whether the encoded output can actually be streamed to disk.
 *
 * Not "does navigator.storage exist": Safari and private-browsing modes
 * expose the API and then refuse createWritable, and a device that is told
 * it may take a 1 GB file and then silently falls back to buffering it in
 * memory is worse off than one that was never offered the choice. So this
 * writes a byte, reads the handle back, and cleans up.
 */
async function supportsStreamingOutput(): Promise<boolean> {
  try {
    const storage = (navigator as unknown as {
      storage?: { getDirectory?: () => Promise<{
        getFileHandle: (n: string, o?: { create?: boolean }) =>
          Promise<{ createWritable: () => Promise<WritableStream> }>
        removeEntry: (n: string) => Promise<void>
      }> }
    }).storage
    if (!storage?.getDirectory) return false
    const dir = await storage.getDirectory()
    const name = "c2c-probe-" + Math.random().toString(36).slice(2, 8)
    try {
      const handle = await dir.getFileHandle(name, { create: true })
      const writable = await handle.createWritable()
      const writer = writable.getWriter()
      await writer.write(new Uint8Array([0]))
      await writer.close()
      return true
    } finally {
      try { await dir.removeEntry(name) } catch { /* nothing to clean */ }
    }
  } catch {
    return false
  }
}

export async function detectCapabilities(): Promise<Capabilities> {
  if (cached) return cached
  cached = await runDetection()
  return cached
}

async function runDetection(): Promise<Capabilities> {
  const isSecureContext = typeof window !== "undefined" && !!window.isSecureContext
  const hasWorker = typeof Worker !== "undefined"
  const hasIndexedDB = typeof indexedDB !== "undefined"
  const hasWebCodecs = typeof (globalThis as Record<string, unknown>).VideoEncoder !== "undefined" &&
                       typeof (globalThis as Record<string, unknown>).VideoDecoder !== "undefined"
  const hasVideoEncoder = typeof (globalThis as Record<string, unknown>).VideoEncoder !== "undefined"

  // navigator.deviceMemory is Chromium-only and returns approximate RAM
  // (0.25 .. 8). Browsers that don't expose it return undefined; we then
  // fall back to the configured ceiling.
  const deviceMemoryGiB =
    typeof navigator !== "undefined" &&
    typeof (navigator as Navigator & { deviceMemory?: number }).deviceMemory === "number"
      ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory
      : undefined

  // WebKit (Safari on iOS/iPadOS, and any WebKit-based browser on those
  // platforms - Chrome/Firefox for iOS are WebKit under the hood too)
  // never exposes navigator.deviceMemory, so memoryLimited below would
  // silently always be false there. Left unguarded, a large encode can
  // push the tab over WebKit's memory ceiling and the OS reloads the
  // page outright, wiping in-progress form state. See T434086.
  //
  // There's no reliable capability check for "is this WebKit on iOS" -
  // it has to be UA sniffing. Matches iPhone/iPad/iPod Safari and any
  // other browser on iOS (they're all WebKit under Apple's App Store
  // rules), and also iPadOS 13+ which disguises itself as "Macintosh"
  // but still lacks deviceMemory and exhibits the same behavior - so we
  // treat "Macintosh" UA + touch support as iPad too.
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || ""
  const isIOSDevice =
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Macintosh") && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1)

  // Treat the device as memory-constrained when it reports under 4 GiB, or
  // when it reports nothing at all and is iOS - WebKit gives no signal, so
  // assuming desktop-class memory there would be optimistic about the one
  // platform that kills the tab without a catchable error.
  const memoryLimited =
    (deviceMemoryGiB !== undefined && deviceMemoryGiB < 4) ||
    (deviceMemoryGiB === undefined && isIOSDevice)

  // What that constraint costs depends on whether the encoded output can go
  // to disk. When it can, a large file is no heavier on memory than a small
  // one - the source is read in ranges and the output never enters the heap
  // - so a limited device gets 1 GB rather than 200 MB. When it cannot, the
  // output accumulates in memory and the conservative figure still applies.
  const streamingOutput = await supportsStreamingOutput()
  const {
    HARD_MAX_FILE_SIZE_BYTES,
    LIMITED_DEVICE_MAX_BYTES_STREAMING,
    LIMITED_DEVICE_MAX_BYTES_BUFFERED
  } = await import("./config")
  const limitedCap = streamingOutput
    ? LIMITED_DEVICE_MAX_BYTES_STREAMING
    : LIMITED_DEVICE_MAX_BYTES_BUFFERED
  const effectiveMaxBytes = memoryLimited
    ? Math.min(HARD_MAX_FILE_SIZE_BYTES, limitedCap)
    : HARD_MAX_FILE_SIZE_BYTES

  let vp9Supported: boolean | null = null
  let reason = ""

  if (!isSecureContext) {
    // crypto.subtle (used for OAuth PKCE) and most modern APIs require
    // a secure context. If we're loaded over plain http://, nothing works.
    reason = "Insecure context: clip2commons must be loaded over HTTPS."
  } else if (!hasWorker) {
    reason = "Web Workers are not available in this browser."
  } else if (!hasWebCodecs) {
    reason = "Your browser does not support the WebCodecs API. " +
             "Try a recent Chromium-based browser, Safari 26.1+, or video2commons."
  } else if (hasVideoEncoder) {
    // Probe whether VP9 encoding specifically is supported. Some browsers
    // expose the VideoEncoder constructor but reject every config.
    try {
      const VE = (globalThis as Record<string, unknown>).VideoEncoder as {
        isConfigSupported: (cfg: unknown) => Promise<{ supported?: boolean }>
      }
      // Use a small probe size - we only need to know if the codec works.
      const probe = await VE.isConfigSupported({
        codec: "vp09.00.10.08",
        width: 640,
        height: 360,
        bitrate: 1_000_000,
        framerate: 30
      })
      vp9Supported = probe?.supported === true
      if (!vp9Supported) {
        reason = "Your browser supports WebCodecs but cannot encode VP9. " +
                 "Already-VP9 WebM files can still be uploaded, but other " +
                 "formats need video2commons or a different browser."
      }
    } catch (err) {
      vp9Supported = false
      reason = "VP9 encoder probe failed: " +
               (err instanceof Error ? err.message : String(err))
    }
  } else {
    reason = "Your browser exposes WebCodecs but no VideoEncoder constructor."
  }

  const canEncode = isSecureContext && hasWorker && hasWebCodecs && hasVideoEncoder && vp9Supported === true
  // Passthrough only needs the upload path; no encoder.
  const canPassthrough = isSecureContext && hasWorker

  return {
    canEncode,
    canPassthrough,
    canResume: hasIndexedDB,
    reason: canEncode ? "" : reason,
    effectiveMaxBytes,
    details: {
      hasWebCodecs,
      hasVideoEncoder,
      vp9Supported,
      hasWorker,
      hasIndexedDB,
      isSecureContext,
      deviceMemoryGiB,
      isIOSDevice,
      memoryLimited
    }
  }
}
